import React, { useState, useEffect, useRef, useCallback } from "react";
import {
	Box,
	Select,
	MenuItem,
	Typography,
	Grid,
	IconButton,
	Tooltip,
	TextField,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { Refresh, ExpandMore, ArrowBack } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useHttpService from "../../customHooks/useHttpService";
import JsonSchemaBox from "../../components/Common/JsonSchemaBox.jsx";
import UserService from "../../services/UserService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localeData from "dayjs/plugin/localeData"; 
import customParseFormat from "dayjs/plugin/customParseFormat";  

const apiGetDashboardPath = "/api/scalar/grafana/Dashboard";
const apiUrlGetDashboard = new URL(
	apiGetDashboardPath,
	process.env.NODE_ENV === "production"
		? window.location.origin + process.env.REACT_APP_PROD_API_BASE_PATH
		: process.env.REACT_APP_DEV_API_BASE_URL +
		  process.env.REACT_APP_DEV_API_BASE_PATH
);

dayjs.extend(utc);
dayjs.extend(localeData);
dayjs.extend(customParseFormat);




const dateFormat = "DD/MM/YYYY HH:mm";
const MAX_IFRAME_HISTORY = 50;
const IFRAME_URL_POLL_MS = 500;
const NEW_HISTORY_DEBOUNCE_MS = 300;
const BACK_VERIFY_MS = 600;
const DEFAULT_KIOSK_MODE = "1";
const DEFAULT_GRAFANA_THEME = "sapphiredusk";
const EMBED_QUERY_PARAMS = ["kiosk", "theme", "hideControls"];

const TRANSIENT_QUERY_PARAMS = new Set([
	"from",
	"to",
	"time",
	"timezone",
	"refresh",
	"orgId",
	"theme",
	"kiosk",
	"hideControls",
]);

const isGrafanaUrl = (url) => url.pathname.includes("/grafana");

const normalizeKioskParam = (url) => {
	if (url.searchParams.get("kiosk") === "") {
		url.searchParams.set("kiosk", DEFAULT_KIOSK_MODE);
	}
};

const getPageFingerprint = (urlString) => {
	try {
		const url = new URL(urlString, window.location.origin);
		const stableParams = [...url.searchParams.entries()]
			.filter(([key]) => !TRANSIENT_QUERY_PARAMS.has(key))
			.filter(([, value]) => value !== "" && value != null)
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([key, value]) => `${key}=${value}`)
			.join("&");

		return `${url.pathname}${url.hash}|${stableParams}`;
	} catch {
		return urlString;
	}
};

const ensureEmbedUrl = (targetUrl, referenceUrl) => {
	if (!targetUrl) return targetUrl;

	try {
		const target = new URL(targetUrl, window.location.origin);
		const reference = referenceUrl
			? new URL(referenceUrl, window.location.origin)
			: null;

		EMBED_QUERY_PARAMS.forEach((key) => {
			if (reference?.searchParams.has(key)) {
				target.searchParams.set(key, reference.searchParams.get(key));
			}
		});

		if (isGrafanaUrl(target) && !target.searchParams.has("kiosk")) {
			target.searchParams.set(
				"kiosk",
				reference?.searchParams.get("kiosk") || DEFAULT_KIOSK_MODE
			);
		}

		if (isGrafanaUrl(target) && !target.searchParams.has("theme")) {
			target.searchParams.set(
				"theme",
				reference?.searchParams.get("theme") || DEFAULT_GRAFANA_THEME
			);
		}

		normalizeKioskParam(target);
		return target.toString();
	} catch {
		return targetUrl;
	}
};

const DataView = ({ section = null }) => {
	const httpService = useHttpService();
	const [filters, setFilters] = useState({});
	const [jsonSchema, setJsonSchema] = useState({});
	const [dashboards, setDashboards] = useState([]);
	const [selectedDashboard, setSelectedDashboard] = useState(null);
	const [iframeKey, setIframeKey] = useState(0);
	const [canGoBack, setCanGoBack] = useState(false);
	const [isError, setIsError] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const iframeRef = useRef(null);
	const iframeHistoryRef = useRef([]);
	const historyIndexRef = useRef(0);
	const isProgrammaticNavRef = useRef(false);
	const isNavigatingBackRef = useRef(false);
	const isApplyingEmbedFixRef = useRef(false);
	const lastRecordedHrefRef = useRef("");
	const popStateHandlerRef = useRef(null);
	const urlPollIntervalRef = useRef(null);
	const newHistoryDebounceRef = useRef(null);
	const backVerifyTimerRef = useRef(null);
	const rootDashboardUrlRef = useRef("");
const [fromDate, setFromDate] = useState(dayjs().utc().subtract(30, "day"));
const [toDate, setToDate] = useState(dayjs().utc());
	const fetchDashboards = () => {
		setIsLoading(true);
		setIsError(false);

		httpService.getAPI(
			apiUrlGetDashboard,
			[],
			(response) => {
				const dashboardsData = Array.isArray(response) ? response : [];
				let visibleDashboards = dashboardsData.filter(
					(dashboard) => dashboard.is_visible
				);
				
				if (section) {
					visibleDashboards = visibleDashboards.filter(
						(dashboard) => dashboard.section && dashboard.section.toLowerCase() === section.toLowerCase()
					);
				}
				
				setDashboards(visibleDashboards);
				setIsLoading(false);
			},
			() => {},
			setIsLoading,
			() => {},
			setIsError
		);
	};

	const handleFilterChange = (newFilters) => {
		setFilters(newFilters);
		setIframeKey((prevKey) => prevKey + 1);
	};

	const getFilteredDashboardUrl = (baseUrl) => {
		if (!baseUrl) return "";

		let url = new URL(baseUrl, window.location.origin);

		if (!url.searchParams.has("orgId")) {
			url.searchParams.set("orgId", "1");
		}

		Object.entries(filters).forEach(([key, value]) => {
			if (value) {
				url.searchParams.set(key, value);
			}
		});

		return ensureEmbedUrl(url.toString(), baseUrl);
	};

	useEffect(() => {
		fetchDashboards();
		const timer = setTimeout(() => {
			setGrafanaJwtCookie();
		}, 500);
		return () => clearTimeout(timer);
	}, [section]);

	useEffect(() => {
		if (selectedDashboard) {
			setGrafanaJwtCookie();
		}
	}, [selectedDashboard]);

	const handleDashboardChange = (event) => {
		const selectedUrl = event.target.value;
		setSelectedDashboard(selectedUrl);

		const selected = dashboards.find((d) => d.url === selectedUrl);
		if (selected && selected.json_schema) {
			setJsonSchema(selected.json_schema);
		} else {
			setJsonSchema({});
		}
	};

	const stopIframeUrlPolling = useCallback(() => {
		if (urlPollIntervalRef.current) {
			clearInterval(urlPollIntervalRef.current);
			urlPollIntervalRef.current = null;
		}
	}, []);

	const detachIframeNavListeners = useCallback(() => {
		try {
			const win = iframeRef.current?.contentWindow;
			if (win && popStateHandlerRef.current) {
				win.removeEventListener("popstate", popStateHandlerRef.current);
				win.removeEventListener("hashchange", popStateHandlerRef.current);
			}
		} catch {
		}
		popStateHandlerRef.current = null;
		try {
			const win = iframeRef.current?.contentWindow;
			if (win) {
				delete win.__dataViewHistoryListener;
			}
		} catch {
		}
	}, []);

	const resetIframeHistory = useCallback(() => {
		iframeHistoryRef.current = [];
		historyIndexRef.current = 0;
		lastRecordedHrefRef.current = "";
		isProgrammaticNavRef.current = false;
		isNavigatingBackRef.current = false;
		isApplyingEmbedFixRef.current = false;
		stopIframeUrlPolling();
		if (newHistoryDebounceRef.current) {
			clearTimeout(newHistoryDebounceRef.current);
			newHistoryDebounceRef.current = null;
		}
		if (backVerifyTimerRef.current) {
			clearTimeout(backVerifyTimerRef.current);
			backVerifyTimerRef.current = null;
		}
		rootDashboardUrlRef.current = "";
		detachIframeNavListeners();
		setCanGoBack(false);
	}, [detachIframeNavListeners, stopIframeUrlPolling]);

	const getEmbedReferenceUrl = useCallback(() => {
		return selectedDashboard ? getFilteredDashboardUrl(selectedDashboard) : "";
	}, [selectedDashboard, filters]);

	const getIframeWindow = useCallback(() => {
		try {
			return iframeRef.current?.contentWindow || null;
		} catch {
			return null;
		}
	}, []);

	const getCurrentIframeFingerprint = useCallback(() => {
		const win = getIframeWindow();
		if (!win) return null;
		try {
			return getPageFingerprint(win.location.href);
		} catch {
			return null;
		}
	}, [getIframeWindow]);

	const getRootDashboardUrl = useCallback(() => {
		return (
			rootDashboardUrlRef.current ||
			getEmbedReferenceUrl() ||
			iframeHistoryRef.current[0]?.url ||
			""
		);
	}, [getEmbedReferenceUrl]);

	const syncCanGoBack = useCallback(() => {
		const stack = iframeHistoryRef.current;
		const currentIndex = historyIndexRef.current;
		const currentFingerprint =
			getCurrentIframeFingerprint() ||
			stack[currentIndex]?.fingerprint ||
			"";

		const rootUrl = getRootDashboardUrl();
		const rootFingerprint = rootUrl ? getPageFingerprint(rootUrl) : "";
		const leftRootView =
			!!rootFingerprint &&
			!!currentFingerprint &&
			rootFingerprint !== currentFingerprint;

		const hasStackPrevious =
			stack.length > 1 &&
			currentIndex > 0 &&
			stack
				.slice(0, currentIndex)
				.some((entry) => entry.fingerprint !== currentFingerprint);

		setCanGoBack(hasStackPrevious || leftRootView);
	}, [getCurrentIframeFingerprint, getRootDashboardUrl]);

	const pushHistoryEntry = useCallback(
		(rawUrl) => {
			const fingerprint = getPageFingerprint(rawUrl);
			const stack = iframeHistoryRef.current;
			const currentIndex = historyIndexRef.current;

			if (!rootDashboardUrlRef.current) {
				rootDashboardUrlRef.current = ensureEmbedUrl(
					rawUrl,
					getEmbedReferenceUrl()
				);
			}

			const nextStack = stack.slice(0, currentIndex + 1);
			nextStack.push({ url: rawUrl, fingerprint });

			if (nextStack.length > MAX_IFRAME_HISTORY) {
				const overflow = nextStack.length - MAX_IFRAME_HISTORY;
				nextStack.splice(0, overflow);
				historyIndexRef.current = Math.max(0, currentIndex + 1 - overflow);
			} else {
				historyIndexRef.current = nextStack.length - 1;
			}

			iframeHistoryRef.current = nextStack;
			lastRecordedHrefRef.current = rawUrl;
			syncCanGoBack();
		},
		[getEmbedReferenceUrl, syncCanGoBack]
	);

	const commitHistoryEntry = useCallback(
		(rawUrl, immediate = false) => {
			const fingerprint = getPageFingerprint(rawUrl);
			const stack = iframeHistoryRef.current;
			const currentIndex = historyIndexRef.current;
			const currentEntry = stack[currentIndex];

			if (currentEntry?.fingerprint === fingerprint) {
				stack[currentIndex] = { url: rawUrl, fingerprint };
				lastRecordedHrefRef.current = rawUrl;
				syncCanGoBack();
				return;
			}

			if (!immediate) {
				if (newHistoryDebounceRef.current) {
					clearTimeout(newHistoryDebounceRef.current);
				}

				newHistoryDebounceRef.current = setTimeout(() => {
					newHistoryDebounceRef.current = null;
					try {
						const liveUrl =
							getIframeWindow()?.location?.href || rawUrl;
						pushHistoryEntry(liveUrl);
					} catch {
						pushHistoryEntry(rawUrl);
					}
				}, NEW_HISTORY_DEBOUNCE_MS);
				return;
			}

			pushHistoryEntry(rawUrl);
		},
		[getIframeWindow, pushHistoryEntry, syncCanGoBack]
	);

	const navigateIframeTo = useCallback((url) => {
		isProgrammaticNavRef.current = true;
		const win = getIframeWindow();
		if (win) {
			try {
				win.location.replace(url);
				return;
			} catch {
			}
		}

		if (iframeRef.current) {
			iframeRef.current.src = url;
		} else {
			isProgrammaticNavRef.current = false;
		}
	}, [getIframeWindow]);

	const applyEmbedFixIfNeeded = useCallback(() => {
		const win = getIframeWindow();
		if (!win || isApplyingEmbedFixRef.current) return false;

		try {
			const currentUrl = win.location.href;
			const embedReference = getEmbedReferenceUrl();
			const fixedUrl = ensureEmbedUrl(currentUrl, embedReference);

			if (fixedUrl !== currentUrl) {
				isApplyingEmbedFixRef.current = true;
				win.location.replace(fixedUrl);
				return true;
			}
		} catch {
		}

		return false;
	}, [getEmbedReferenceUrl, getIframeWindow]);

	const findPreviousHistoryIndex = useCallback((stack, fromIndex, currentFingerprint) => {
		for (let index = fromIndex - 1; index >= 0; index -= 1) {
			if (stack[index].fingerprint !== currentFingerprint) {
				return index;
			}
		}
		return -1;
	}, []);

	const navigateToRootDashboard = useCallback(() => {
		const embedReference = getEmbedReferenceUrl();
		const rootUrl = ensureEmbedUrl(getRootDashboardUrl(), embedReference);
		if (!rootUrl) return;

		const fingerprint = getPageFingerprint(rootUrl);
		iframeHistoryRef.current = [{ url: rootUrl, fingerprint }];
		historyIndexRef.current = 0;
		lastRecordedHrefRef.current = rootUrl;
		if (!rootDashboardUrlRef.current) {
			rootDashboardUrlRef.current = rootUrl;
		}
		navigateIframeTo(rootUrl);
	}, [getEmbedReferenceUrl, getRootDashboardUrl, navigateIframeTo]);

	const navigateToStackIndex = useCallback(
		(targetIndex) => {
			const stack = iframeHistoryRef.current;
			if (targetIndex < 0 || !stack[targetIndex]) return false;

			const embedReference = getEmbedReferenceUrl() || stack[0]?.url;
			const previousUrl = ensureEmbedUrl(stack[targetIndex].url, embedReference);

			iframeHistoryRef.current = stack.slice(0, targetIndex + 1);
			historyIndexRef.current = targetIndex;
			lastRecordedHrefRef.current = previousUrl;
			navigateIframeTo(previousUrl);
			return true;
		},
		[getEmbedReferenceUrl, navigateIframeTo]
	);

	const scheduleBackVerification = useCallback(
		(fingerprintBeforeBack) => {
			if (backVerifyTimerRef.current) {
				clearTimeout(backVerifyTimerRef.current);
			}

			backVerifyTimerRef.current = setTimeout(() => {
				backVerifyTimerRef.current = null;

				const fingerprintAfterBack = getCurrentIframeFingerprint();
				if (
					fingerprintBeforeBack &&
					fingerprintAfterBack &&
					fingerprintBeforeBack === fingerprintAfterBack
				) {
					isNavigatingBackRef.current = true;
					navigateToRootDashboard();
				}

				syncCanGoBack();
			}, BACK_VERIFY_MS);
		},
		[getCurrentIframeFingerprint, navigateToRootDashboard, syncCanGoBack]
	);

	const handleIframeBack = useCallback(() => {
		if (
			isNavigatingBackRef.current ||
			isProgrammaticNavRef.current ||
			isApplyingEmbedFixRef.current
		) {
			return;
		}

		if (newHistoryDebounceRef.current) {
			clearTimeout(newHistoryDebounceRef.current);
			newHistoryDebounceRef.current = null;
		}

		const stack = iframeHistoryRef.current;
		const currentIndex = historyIndexRef.current;
		const currentFingerprint =
			getCurrentIframeFingerprint() ||
			stack[currentIndex]?.fingerprint ||
			"";

		const rootUrl = getRootDashboardUrl();
		const rootFingerprint = rootUrl ? getPageFingerprint(rootUrl) : "";
		const leftRootView =
			!!rootFingerprint &&
			!!currentFingerprint &&
			rootFingerprint !== currentFingerprint;

		let targetIndex = findPreviousHistoryIndex(
			stack,
			currentIndex,
			currentFingerprint
		);

		if (targetIndex < 0 && leftRootView) {
			isNavigatingBackRef.current = true;
			navigateToRootDashboard();
			return;
		}

		if (targetIndex < 0) return;

		isNavigatingBackRef.current = true;
		const didNavigate = navigateToStackIndex(targetIndex);
		if (didNavigate) {
			scheduleBackVerification(currentFingerprint);
		} else if (leftRootView) {
			navigateToRootDashboard();
		} else {
			isNavigatingBackRef.current = false;
		}
	}, [
		findPreviousHistoryIndex,
		getCurrentIframeFingerprint,
		getRootDashboardUrl,
		navigateToRootDashboard,
		navigateToStackIndex,
		scheduleBackVerification,
	]);

	const processIframeNavigation = useCallback(() => {
		const win = getIframeWindow();
		if (!win) return;

		try {
			if (applyEmbedFixIfNeeded()) {
				return;
			}
			isApplyingEmbedFixRef.current = false;

			const currentUrl = win.location.href;

			if (isProgrammaticNavRef.current || isNavigatingBackRef.current) {
				isProgrammaticNavRef.current = false;
				isNavigatingBackRef.current = false;
				lastRecordedHrefRef.current = currentUrl;
				syncCanGoBack();
				return;
			}

			const isFirstEntry = iframeHistoryRef.current.length === 0;
			commitHistoryEntry(currentUrl, isFirstEntry);
		} catch {
			setCanGoBack(false);
		}
	}, [
		applyEmbedFixIfNeeded,
		commitHistoryEntry,
		getIframeWindow,
		syncCanGoBack,
	]);

	const trackIframeUrlChange = useCallback(() => {
		if (
			isNavigatingBackRef.current ||
			isProgrammaticNavRef.current ||
			isApplyingEmbedFixRef.current
		) {
			return;
		}

		const win = getIframeWindow();
		if (!win) return;

		try {
			const href = win.location.href;
			if (!href || href === lastRecordedHrefRef.current) return;

			const fingerprint = getPageFingerprint(href);
			const stack = iframeHistoryRef.current;
			const currentIndex = historyIndexRef.current;
			const currentEntry = stack[currentIndex];

			if (currentEntry?.fingerprint === fingerprint) {
				stack[currentIndex] = { url: href, fingerprint };
				lastRecordedHrefRef.current = href;
				return;
			}

			commitHistoryEntry(href);
		} catch {
		}
	}, [commitHistoryEntry, getIframeWindow]);

	const startIframeUrlPolling = useCallback(() => {
		stopIframeUrlPolling();
		urlPollIntervalRef.current = setInterval(
			trackIframeUrlChange,
			IFRAME_URL_POLL_MS
		);
	}, [stopIframeUrlPolling, trackIframeUrlChange]);

	const handleRefresh = () => {
		setGrafanaJwtCookie();
		setIframeKey((prevKey) => prevKey + 1);
	};

	useEffect(() => {
		resetIframeHistory();
	}, [selectedDashboard, iframeKey, resetIframeHistory]);

	useEffect(() => {
		return () => {
			resetIframeHistory();
		};
	}, [resetIframeHistory]);

	const attachIframeNavListeners = useCallback(() => {
		const win = getIframeWindow();
		if (!win || win.__dataViewHistoryListener) return;

		popStateHandlerRef.current = () => {
			processIframeNavigation();
		};
		win.__dataViewHistoryListener = true;
		win.addEventListener("popstate", popStateHandlerRef.current);
		win.addEventListener("hashchange", popStateHandlerRef.current);
	}, [getIframeWindow, processIframeNavigation]);

	const handleIframeLoad = useCallback(() => {
		attachIframeNavListeners();
		processIframeNavigation();
		startIframeUrlPolling();
	}, [
		attachIframeNavListeners,
		processIframeNavigation,
		startIframeUrlPolling,
	]);

	const setGrafanaJwtCookie = () => {
		if (UserService.isLoggedIn()) {
			const token = UserService.getToken();
			if (token && typeof token === 'string' && token.trim() !== '') {
				const isSecure = window.location.protocol === "https:";
				const cookieValue = `jwt=${token}; path=/grafana; ${isSecure ? "Secure; " : ""}SameSite=Strict`;
				document.cookie = cookieValue;
			} else {
				console.warn('Grafana JWT cookie not set: token is undefined, null, or empty');
			}
		}
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 64px)",
					overflow: "hidden",
					p: 2,
				}}
			>
				<Typography variant="h6" gutterBottom>
					{section 
						? section === "operative" 
							? "Operative Data View"
							: `${section.charAt(0).toUpperCase() + section.slice(1)} Data View`
						: "Grafana Dashboard Viewer"}
				</Typography>
				<Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
					<Grid item xs={12} md={4}>
						<Select
							fullWidth
							value={selectedDashboard || ""}
							onChange={handleDashboardChange}
							displayEmpty
						>
							{dashboards.length > 0 ? (
								dashboards.map((dashboard) => (
									<MenuItem key={dashboard.id} value={dashboard.url}>
										{dashboard.name}
									</MenuItem>
								))
							) : (
								<MenuItem disabled>No dashboards available</MenuItem>
							)}
						</Select>
					</Grid>

					{Object.keys(jsonSchema).length > 0 && (
						<>
							<Grid item>
								<DateTimePicker
									label="From Date"
									value={fromDate ? dayjs(fromDate).local() : null}
									onChange={(newValue) =>
										setFromDate(newValue ? dayjs(newValue).utc() : null)
									}
									renderInput={(params) => <TextField {...params} />}
									format={dateFormat}
								/>
							</Grid>

							<Grid item>
								<DateTimePicker
									label="To Date"
									value={toDate ? dayjs(toDate).local() : null}
									onChange={(newValue) =>
										setToDate(newValue ? dayjs(newValue).utc() : null)
									}
									renderInput={(params) => <TextField {...params} />}
									format={dateFormat}
								/>
							</Grid>
						</>
					)}

					<Grid item sx={{ display: "flex", alignItems: "center" }}>
						<Tooltip arrow title={canGoBack ? "Back" : "No previous page in dashboard"}>
							<span>
								<IconButton onClick={handleIframeBack} disabled={!canGoBack}>
									<ArrowBack />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip arrow title="Refresh">
							<IconButton onClick={handleRefresh}>
								<Refresh />
							</IconButton>
						</Tooltip>
					</Grid>
				</Grid>
				{selectedDashboard &&
					jsonSchema &&
					Object.keys(jsonSchema).length > 0 && (
						<Accordion
							sx={{ mb: 2, backgroundColor: "#3d3d3d", color: "white" }}
							defaultExpanded={false}
						>
							<AccordionSummary
								expandIcon={<ExpandMore />}
								sx={{ color: "white" }}
							>
								<Typography>Advanced Filters</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<JsonSchemaBox
									schema={jsonSchema}
									onChange={handleFilterChange}
								/>
							</AccordionDetails>
						</Accordion>
					)}
				{isLoading && (
					<Typography color="primary">Loading dashboards...</Typography>
				)}
				{isError && (
					<Typography color="error">Error loading dashboards</Typography>
				)}

				{selectedDashboard && !isError && (
					<Box
						sx={{
							flexGrow: 1,
							border: "1px solid #ccc",
							borderRadius: 2,
							overflow: "hidden",
						}}
					>
						<iframe
							ref={iframeRef}
							key={iframeKey}
							src={getFilteredDashboardUrl(selectedDashboard)}
							width="100%"
							height="100%"
							title="Grafana Dashboard"
							style={{ border: "none" }}
							onLoad={handleIframeLoad}
						></iframe>
					</Box>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default DataView;
